import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import citiesData from '../../data/cities_with_coordinates.json';
import UserAvatar from './UserAvatar';
import { API_BASE_URL } from '../../lib/apiConfig';
import { apiFetch } from '../../lib/apiFetch';
import DemandDetailModalMobile from './DemandDetailModalMobile';

type DemandStatus = 'NAO_INICIADA' | 'EM_ANDAMENTO' | 'PAUSADA' | 'CANCELADA' | 'CONCLUIDA';
type DemandType = 'INDIVIDUAL' | 'CIDADE' | 'ENTIDADE' | 'GRUPO';
type DemandSize = 'PEQUENA' | 'MEDIA' | 'GRANDE';
type DemandNature = 'EXCLUSIVA' | 'INTERMEDIADA';

interface Demand {
  id: number;
  titulo: string;
  descricao?: string;
  tipo: DemandType;
  tamanho?: DemandSize;
  natureza?: DemandNature;
  status: DemandStatus;
  data_cadastro: string;
  previsao_conclusao?: string;
  cidade_id?: number | null;
  solicitante_id?: number | null;
  cidade?: { id?: number; nome: string };
  solicitante?: { id?: number; nome: string };
  responsavel?: { id: number; nome: string; foto_url?: string | null };
  intermediador?: string;
}

interface Usuario {
  id: number;
  nome: string;
  tipo_usuario: 'ADMIN' | 'ASSESSOR';
  nivel_acesso: string;
  foto_url?: string | null;
}

interface Registro {
  id: number;
  descricao_atualizacao: string;
  data: string;
  status_na_data?: DemandStatus;
  created_at: string;
  updated_at: string;
  usuario: {
    id: number;
    nome: string;
  };
}

interface HistoricoItem {
  id: number;
  observacao: string;
  data_hora: string;
  usuario: {
    id: number;
    nome: string;
  } | null;
}

interface DemandDetailModalProps {
  isOpen: boolean;
  demand: Demand | null;
  onClose: () => void;
  onUpdate: () => void;
  activeTab?: 'info' | 'registros';
  onTabChange?: (tab: 'info' | 'registros') => void;
  registros?: Registro[];
  loadingRegistros?: boolean;
  newRegistro?: string;
  onNewRegistroChange?: (value: string) => void;
  onAddRegistro?: () => void;
  addingRegistro?: boolean;
  editingRegistro?: number | null;
  editingText?: string;
  onEditRegistro?: (id: number) => void;
  onSaveEdit?: (id: number) => void;
  onCancelEdit?: () => void;
  onEditTextChange?: (value: string) => void;
  onDeleteRegistro?: (id: number) => void;
  savingRegistroId?: number | null;
  deletingRegistroId?: number | null;
  openMenuId?: number | null;
  onMenuToggle?: (id: number | null) => void;
  canViewUpdates?: boolean;
  canCreateUpdates?: boolean;
  canEditUpdate?: (registro: Registro) => boolean;
  canDeleteUpdate?: (registro: Registro) => boolean;
  canEditDemand?: boolean;
  canDeleteDemand?: boolean;
  canAssignResponsible?: boolean;
  canUnassignResponsible?: boolean;
  userData?: Usuario | null;
  historico?: HistoricoItem[];
  loadingHistorico?: boolean;
  slideOver?: boolean;
  contained?: boolean;
}

const slideInFromRight = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const slideOutToRight = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

const statusOptions: { value: DemandStatus; label: string }[] = [
  { value: 'NAO_INICIADA', label: 'Não Iniciada' },
  { value: 'EM_ANDAMENTO', label: 'Em Andamento' },
  { value: 'PAUSADA', label: 'Pausada' },
  { value: 'CANCELADA', label: 'Cancelada' },
  { value: 'CONCLUIDA', label: 'Concluída' }
];

const typeLabels: Record<DemandType, string> = {
  INDIVIDUAL: 'Individual',
  CIDADE: 'Cidade',
  ENTIDADE: 'Entidade',
  GRUPO: 'Grupo'
};

const sizeLabels: Record<DemandSize, string> = {
  PEQUENA: 'Pequena',
  MEDIA: 'Média',
  GRANDE: 'Grande'
};

const natureLabels: Record<DemandNature, string> = {
  EXCLUSIVA: 'Exclusiva',
  INTERMEDIADA: 'Intermediada'
};

const statusLabels: Record<DemandStatus, string> = {
  NAO_INICIADA: 'Não Iniciada',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CANCELADA: 'Cancelada',
  CONCLUIDA: 'Concluída'
};

export const DemandDetailModal: React.FC<DemandDetailModalProps> = ({
  isOpen,
  demand,
  onClose,
  onUpdate,
  activeTab = 'info',
  onTabChange,
  registros = [],
  loadingRegistros = false,
  newRegistro = '',
  onNewRegistroChange,
  onAddRegistro,
  addingRegistro = false,
  editingRegistro = null,
  editingText = '',
  onEditRegistro,
  onSaveEdit,
  onCancelEdit,
  onEditTextChange,
  onDeleteRegistro,
  savingRegistroId = null,
  deletingRegistroId = null,
  openMenuId = null,
  onMenuToggle,
  canViewUpdates = false,
  canCreateUpdates = false,
  canEditUpdate,
  canDeleteUpdate,
  canEditDemand = false,
  canDeleteDemand = false,
  canAssignResponsible = false,
  canUnassignResponsible = false,
  userData: userDataProp,
  historico = [],
  loadingHistorico = false,
  slideOver = false,
  contained = false,
}) => {
  const { session, userData: authUserData } = useAuth();
  const userData = userDataProp || authUserData; // Use prop if provided, otherwise fallback to context
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [openResponsibleDropdown, setOpenResponsibleDropdown] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const responsibleDropdownRef = useRef<HTMLDivElement>(null);
  const [assigningResponsible, setAssigningResponsible] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentDemand, setCurrentDemand] = useState<Demand | null>(null);
  const [savingField, setSavingField] = useState<keyof Demand | null>(null);
  const [eleitores, setEleitores] = useState<Array<{ id: number; nome: string }>>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteRegistroId, setPendingDeleteRegistroId] = useState<number | null>(null);
  const [registrosFilter, setRegistrosFilter] = useState<'tudo' | 'comentarios' | 'historico'>('tudo');
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    if (slideOver) {
      setIsClosing(true);
      setTimeout(() => { setIsClosing(false); onClose(); }, 320);
    } else {
      onClose();
    }
  };

  // Auto-close the registro confirm modal once deletion finishes
  useEffect(() => {
    if (deletingRegistroId === null && pendingDeleteRegistroId !== null) {
      setPendingDeleteRegistroId(null);
    }
  }, [deletingRegistroId]);

  const getPhotoUrl = (userId: number) => {
    return `https://i.pravatar.cc/150?u=${userId}`;
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!openStatusDropdown && !openResponsibleDropdown) return;
    const handler = (e: MouseEvent) => {
      if (openStatusDropdown && statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setOpenStatusDropdown(false);
      }
      if (openResponsibleDropdown && responsibleDropdownRef.current && !responsibleDropdownRef.current.contains(e.target as Node)) {
        setOpenResponsibleDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openStatusDropdown, openResponsibleDropdown]);

  useEffect(() => {
    if (openMenuId === null) return;
    const handler = (e: MouseEvent) => {
      if (!(e.target as Element).closest('[data-registro-menu]')) {
        onMenuToggle && onMenuToggle(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openMenuId]);

  useEffect(() => {
    if (isOpen && demand) {
      setCurrentDemand(demand);
    }
  }, [isOpen, demand]);

  const fetchUsuarios = async () => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/grupo`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUsuarios(result.data);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    }
  };

  const fetchEleitores = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/eleitores`, {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`
        }
      });
      const data = await response.json();
      // Handle both direct array and wrapped response formats
      setEleitores(Array.isArray(data) ? data : (data.data || []));
    } catch (error) {
      console.error('Erro ao buscar eleitores:', error);
      setEleitores([]);
    }
  };

  const handleAssignResponsible = async (userId: number) => {
    if (!currentDemand) return;

    setAssigningResponsible(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          responsavel_id: userId
        })
      });

      if (response.ok) {
        const assignedUser = usuarios.find(u => u.id === userId);
        if (assignedUser) {
          setCurrentDemand({
            ...currentDemand,
            responsavel: {
              id: assignedUser.id,
              nome: assignedUser.nome
            }
          });
        }
        onUpdate();
        setOpenResponsibleDropdown(false);
      } else {
        const error = await response.json();
        alert(`Erro ao atribuir responsável: ${error.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao atribuir responsável:', error);
      alert('Erro ao atribuir responsável');
    } finally {
      setAssigningResponsible(false);
    }
  };

  const handleUnassignResponsible = async () => {
    if (!currentDemand) return;

    setAssigningResponsible(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ responsavel_id: null })
      });

      if (response.ok) {
        setCurrentDemand({ ...currentDemand, responsavel: undefined });
        onUpdate();
        setOpenResponsibleDropdown(false);
      } else {
        const error = await response.json();
        alert(`Erro ao remover responsável: ${error.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao remover responsável:', error);
      alert('Erro ao remover responsável');
    } finally {
      setAssigningResponsible(false);
    }
  };

  const handleUpdateStatus = async (newStatus: DemandStatus) => {
    if (!currentDemand) return;

    setUpdatingStatus(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus
        })
      });

      if (response.ok) {
        setCurrentDemand({
          ...currentDemand,
          status: newStatus
        });
        onUpdate();
        setOpenStatusDropdown(false);
      } else {
        const error = await response.json();
        alert(`Erro ao atualizar status: ${error.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const saveField = async (
    fieldKey: keyof Demand,
    body: Record<string, any>,
    localPatch?: Partial<Demand>
  ) => {
    if (!currentDemand || !session?.access_token) return;

    setSavingField(fieldKey);
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) throw new Error('Erro ao atualizar demanda');

      setCurrentDemand({ ...currentDemand, ...(localPatch ?? body) } as Demand);
      onUpdate();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('Erro ao salvar alteração');
    } finally {
      setSavingField(null);
    }
  };

  const handleDelete = async () => {
    if (!currentDemand || !session?.access_token) return;

    setIsDeleting(true);
    setShowDeleteConfirm(false);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/demandas/${currentDemand.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (!response.ok) throw new Error('Erro ao excluir demanda');

      onUpdate();
      onClose();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir demanda');
    } finally {
      setIsDeleting(false);
    }
  };

  const calculateSLA = (dataCadastro: string, status: DemandStatus): number => {
    if (status === 'CONCLUIDA' || status === 'CANCELADA') {
      return 0;
    }
    
    const cadastro = new Date(dataCadastro);
    const hoje = new Date();
    const diffTime = Math.abs(hoje.getTime() - cadastro.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getSLAColor = (days: number): string => {
    if (days === 0) return '#10b981';
    if (days <= 7) return '#10b981';
    if (days <= 30) return '#f59e0b';
    return '#ef4444';
  };

  // Always render DemandDetailModalMobile (never unmount) so styled-components
  // register once at page load — eliminates the cold-mount delay on first open.
  const mobileProps = {
    isOpen, demand, onClose, onUpdate,
    registros, loadingRegistros,
    newRegistro, onNewRegistroChange, onAddRegistro, addingRegistro,
    editingRegistro, editingText, onEditRegistro, onSaveEdit, onCancelEdit,
    onEditTextChange, onDeleteRegistro, savingRegistroId, deletingRegistroId,
    openMenuId, onMenuToggle,
    canViewUpdates, canCreateUpdates, canEditUpdate, canDeleteUpdate,
    canEditDemand, canDeleteDemand, canAssignResponsible, canUnassignResponsible,
    userData: userData as any, historico, loadingHistorico,
  };

  if (!isOpen || !currentDemand) return <DemandDetailModalMobile {...mobileProps} />;

  const sla = calculateSLA(currentDemand.data_cadastro, currentDemand.status);
  const slaColor = getSLAColor(sla);


  const innerContent = (
    <>
      <ModalHeader>
          {contained && (
            <BackButton onClick={handleClose}>
              <svg fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Voltar
            </BackButton>
          )}
          <InlineEditText
            value={currentDemand.titulo || ''}
            canEdit={canEditDemand}
            saving={savingField === 'titulo'}
            onSave={(v) => saveField('titulo', { titulo: v })}
            placeholder="Título da demanda"
            allowEmpty={false}
            inputStyle={{ fontSize: '24px', fontWeight: 700, padding: '8px 12px' }}
            renderDisplay={(v) => <ModalTitle>{v}</ModalTitle>}
            displayBlock
          />
          {canEditDemand && currentDemand.status !== 'CONCLUIDA' && (
            <ConcluirButton
              onClick={() => handleUpdateStatus('CONCLUIDA')}
              disabled={updatingStatus}
            >
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M5 13l4 4L19 7" />
              </svg>
              Marcar como concluída
            </ConcluirButton>
          )}
          <HeaderStatusCell
            ref={statusDropdownRef}
            $canEdit={canEditDemand}
            onClick={(e) => {
              if (canEditDemand) {
                e.stopPropagation();
                setOpenStatusDropdown(!openStatusDropdown);
                setOpenResponsibleDropdown(false);
              }
            }}
          >
            {updatingStatus ? (
              <SpinnerCircle />
            ) : (
              <>
                <StatusBadge status={currentDemand.status}>
                  {statusLabels[currentDemand.status]}
                </StatusBadge>
                {canEditDemand && (
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
                    style={{ width: '12px', height: '12px', marginLeft: '4px' }}>
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </>
            )}
            {openStatusDropdown && canEditDemand && (
              <StatusDropdown onClick={(e) => e.stopPropagation()}>
                <StatusOption $status="NAO_INICIADA" onClick={() => handleUpdateStatus('NAO_INICIADA')}>
                  <StatusOptionPill $status="NAO_INICIADA">{statusLabels['NAO_INICIADA']}</StatusOptionPill>
                </StatusOption>
                <StatusOption $status="EM_ANDAMENTO" onClick={() => handleUpdateStatus('EM_ANDAMENTO')}>
                  <StatusOptionPill $status="EM_ANDAMENTO">{statusLabels['EM_ANDAMENTO']}</StatusOptionPill>
                </StatusOption>
                <StatusOption $status="PAUSADA" onClick={() => handleUpdateStatus('PAUSADA')}>
                  <StatusOptionPill $status="PAUSADA">{statusLabels['PAUSADA']}</StatusOptionPill>
                </StatusOption>
                <StatusOption $status="CONCLUIDA" onClick={() => handleUpdateStatus('CONCLUIDA')}>
                  <StatusOptionPill $status="CONCLUIDA">{statusLabels['CONCLUIDA']}</StatusOptionPill>
                </StatusOption>
                <StatusOption $status="CANCELADA" onClick={() => handleUpdateStatus('CANCELADA')}>
                  <StatusOptionPill $status="CANCELADA">{statusLabels['CANCELADA']}</StatusOptionPill>
                </StatusOption>
              </StatusDropdown>
            )}
          </HeaderStatusCell>
          {!contained && <CloseButton onClick={handleClose}>&times;</CloseButton>}
        </ModalHeader>

        <TabsContainer>
          <Tab 
            $active={activeTab === 'info'} 
            onClick={() => onTabChange && onTabChange('info')}
          >
            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Informações
          </Tab>
          {canViewUpdates && (
            <Tab 
              $active={activeTab === 'registros'} 
              onClick={() => {
                onTabChange && onTabChange('registros');
                if (currentDemand?.id && registros.length === 0 && !loadingRegistros) {
                  // Carregar registros quando a tab for aberta
                }
              }}
            >
              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Registros ({registros.length})
            </Tab>
          )}
        </TabsContainer>

        <ModalLayout>
        <ModalBody data-modal-body>
          <LeftPanelContent $hidden={activeTab === 'registros'}>
              <Section>
                <DescriptionHeader>Descrição</DescriptionHeader>
                <InlineEditTextArea
                  value={currentDemand.descricao || ''}
                  canEdit={canEditDemand}
                  saving={savingField === 'descricao'}
                  onSave={(v) => saveField('descricao', { descricao: v })}
                  placeholder="Descreva os detalhes da demanda..."
                  emptyLabel="Sem descrição"
                />
              </Section>
              <div>
              <PropertiesSection>
                <PropertiesLabel>Detalhes</PropertiesLabel>
                <PropertiesGrid>
                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Responsável
                    </PropertyLabel>
                    <PropertyValue>
                      <ResponsibleInline
                        ref={responsibleDropdownRef}
                        $canEdit={canAssignResponsible || canUnassignResponsible}
                        onClick={(e) => {
                          if (canAssignResponsible || canUnassignResponsible) {
                            e.stopPropagation();
                            if (usuarios.length === 0) fetchUsuarios();
                            setOpenResponsibleDropdown(!openResponsibleDropdown);
                            setOpenStatusDropdown(false);
                          }
                        }}
                      >
                        {assigningResponsible ? (
                          <SpinnerCircle />
                        ) : currentDemand.responsavel ? (
                          <>
                            <UserAvatar
                              name={currentDemand.responsavel.nome}
                              fotoUrl={currentDemand.responsavel.foto_url}
                              size={24}
                            />
                            <ResponsibleInlineName>{currentDemand.responsavel.nome}</ResponsibleInlineName>
                          </>
                        ) : (
                          <UnassignedText>
                            {canAssignResponsible ? 'Atribuir' : 'Sem responsável'}
                          </UnassignedText>
                        )}
                        {(canAssignResponsible || canUnassignResponsible) && openResponsibleDropdown && !assigningResponsible && (
                          <ResponsibleDropdown onClick={(e) => e.stopPropagation()}>
                            {currentDemand.responsavel && canUnassignResponsible && (
                              <ResponsibleOption
                                key="unassign"
                                onClick={handleUnassignResponsible}
                                style={{ borderBottom: canAssignResponsible ? '1px solid #f1f5f9' : undefined }}
                              >
                                <OptionAvatar style={{ background: '#f1f5f9' }}>
                                  <svg fill="none" stroke="#94a3b8" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                                    <path d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
                                  </svg>
                                </OptionAvatar>
                                <OptionInfo>
                                  <OptionName style={{ color: '#64748b', fontWeight: 500 }}>Remover atribuição</OptionName>
                                </OptionInfo>
                              </ResponsibleOption>
                            )}
                            {canAssignResponsible && usuarios.map((member) => (
                              <ResponsibleOption
                                key={member.id}
                                onClick={() => handleAssignResponsible(member.id)}
                              >
                                <OptionAvatar>
                                  <UserAvatar name={member.nome} fotoUrl={member.foto_url} size={36} />
                                </OptionAvatar>
                                <OptionInfo>
                                  <OptionName>{member.nome}</OptionName>
                                  <OptionRole>
                                    {member.tipo_usuario === 'ADMIN' ? 'Administrador' : `Assessor · ${member.nivel_acesso}`}
                                  </OptionRole>
                                </OptionInfo>
                              </ResponsibleOption>
                            ))}
                          </ResponsibleDropdown>
                        )}
                      </ResponsibleInline>
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Cidade
                    </PropertyLabel>
                    <PropertyValue>
                      <InlineEditSelect
                        value={currentDemand.cidade_id != null ? String(currentDemand.cidade_id) : undefined}
                        canEdit={canEditDemand}
                        saving={savingField === 'cidade_id'}
                        options={(citiesData as any[])
                          .filter(city => city.microrregiao?.mesorregiao?.UF?.sigla === 'RS')
                          .map(city => ({ value: String(city.id), label: city.nome }))}
                        searchable
                        searchPlaceholder="Buscar cidade..."
                        emptyLabel="-"
                        selectPlaceholder="Selecione"
                        onSave={(v) => {
                          const id = parseInt(v, 10);
                          const city = (citiesData as any[]).find(c => c.id === id);
                          return saveField(
                            'cidade_id',
                            { cidade_id: id },
                            { cidade_id: id, cidade: { id, nome: city?.nome ?? '' } }
                          );
                        }}
                      />
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 11-8 0 4 4 0 018 0zM21 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      Solicitante
                    </PropertyLabel>
                    <PropertyValue>
                      <InlineEditSelect
                        value={currentDemand.solicitante_id != null ? String(currentDemand.solicitante_id) : undefined}
                        canEdit={canEditDemand}
                        saving={savingField === 'solicitante_id'}
                        options={eleitores.map(e => ({ value: String(e.id), label: e.nome }))}
                        searchable
                        searchPlaceholder="Buscar solicitante..."
                        emptyLabel="-"
                        selectPlaceholder="Selecione"
                        onOpen={() => { if (eleitores.length === 0) fetchEleitores(); }}
                        onSave={(v) => {
                          const id = parseInt(v, 10);
                          const eleitor = eleitores.find(e => e.id === id);
                          return saveField(
                            'solicitante_id',
                            { solicitante_id: id },
                            { solicitante_id: id, solicitante: { id, nome: eleitor?.nome ?? '' } }
                          );
                        }}
                      />
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Tipo
                    </PropertyLabel>
                    <PropertyValue>
                      <InlineEditSelect
                        value={currentDemand.tipo}
                        canEdit={canEditDemand}
                        saving={savingField === 'tipo'}
                        options={[
                          { value: 'INDIVIDUAL', label: 'Individual' },
                          { value: 'CIDADE', label: 'Cidade' },
                          { value: 'ENTIDADE', label: 'Entidade' },
                          { value: 'GRUPO', label: 'Grupo' }
                        ]}
                        emptyLabel="-"
                        onSave={(v) => saveField('tipo', { tipo: v })}
                      />
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                      </svg>
                      Natureza
                    </PropertyLabel>
                    <PropertyValue>
                      <InlineEditSelect
                        value={currentDemand.natureza}
                        canEdit={canEditDemand}
                        saving={savingField === 'natureza'}
                        options={[
                          { value: 'EXCLUSIVA', label: 'Exclusiva' },
                          { value: 'INTERMEDIADA', label: 'Intermediada' }
                        ]}
                        emptyLabel="-"
                        onSave={(v) => saveField(
                          'natureza',
                          v === 'EXCLUSIVA' ? { natureza: v, intermediador: null } : { natureza: v },
                          v === 'EXCLUSIVA' ? { natureza: v as DemandNature, intermediador: undefined } : { natureza: v as DemandNature }
                        )}
                      />
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                      Tamanho
                    </PropertyLabel>
                    <PropertyValue>
                      <InlineEditSelect
                        value={currentDemand.tamanho}
                        canEdit={canEditDemand}
                        saving={savingField === 'tamanho'}
                        options={[
                          { value: 'PEQUENA', label: 'Pequena' },
                          { value: 'MEDIA', label: 'Média' },
                          { value: 'GRANDE', label: 'Grande' }
                        ]}
                        emptyLabel="-"
                        onSave={(v) => saveField('tamanho', { tamanho: v })}
                      />
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Previsão
                    </PropertyLabel>
                    <PropertyValue>
                      <InlineEditDate
                        value={currentDemand.previsao_conclusao}
                        canEdit={canEditDemand}
                        saving={savingField === 'previsao_conclusao'}
                        onSave={(v) => saveField('previsao_conclusao', { previsao_conclusao: v })}
                        emptyLabel="-"
                      />
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      SLA
                    </PropertyLabel>
                    <PropertyValue>
                      <SLABadge color={slaColor}>
                        {sla === 0 ? 'Concluída' : `${sla} dias`}
                      </SLABadge>
                    </PropertyValue>
                  </PropertyRow>

                  <PropertyRow>
                    <PropertyLabel>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Cadastrado
                    </PropertyLabel>
                    <PropertyValue style={{ fontSize: '13px', color: '#374151' }}>
                      {new Date(currentDemand.data_cadastro).toLocaleDateString('pt-BR')}
                    </PropertyValue>
                  </PropertyRow>

                  {(currentDemand.intermediador || currentDemand.natureza === 'INTERMEDIADA') && (
                    <PropertyRow>
                      <PropertyLabel>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Intermediador
                      </PropertyLabel>
                      <PropertyValue>
                        <InlineEditText
                          value={currentDemand.intermediador || ''}
                          canEdit={canEditDemand}
                          saving={savingField === 'intermediador'}
                          onSave={(v) => saveField('intermediador', { intermediador: v })}
                          placeholder="Nome do intermediador"
                          emptyLabel="-"
                        />
                      </PropertyValue>
                    </PropertyRow>
                  )}
                </PropertiesGrid>
              </PropertiesSection>
              </div>

          {canDeleteDemand && (
            <DangerZone>
              <DangerZoneDescription>
                Esta demanda não pode ser recuperada após exclusão.
              </DangerZoneDescription>
              <DeleteButton onClick={() => setShowDeleteConfirm(true)} disabled={isDeleting}>
                <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Excluir demanda
              </DeleteButton>
            </DangerZone>
          )}
            </LeftPanelContent>
            <DesktopOnlyRegistrosTab>
            {/* Mobile-only registros tab */}
            {activeTab === 'registros' && (
            <RegistrosContainer>
              {canCreateUpdates && (
                <NewRegistroSection>
                  <NewRegistroTextArea
                    placeholder="Adicionar um novo registro..."
                    value={newRegistro}
                    onChange={(e) => onNewRegistroChange && onNewRegistroChange(e.target.value)}
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        onAddRegistro && onAddRegistro();
                      }
                    }}
                  />
                  <AddRegistroButton
                    onClick={() => onAddRegistro && onAddRegistro()}
                    disabled={!newRegistro.trim() || addingRegistro}
                  >
                    {addingRegistro ? 'Adicionando...' : 'Adicionar'}
                  </AddRegistroButton>
                </NewRegistroSection>
              )}

              {loadingRegistros ? (
                <RegistrosList>
                  {[1, 2, 3].map((i) => (
                    <ShimmerRegistroItem key={i}>
                      <ShimmerRegistroHeader>
                        <ShimmerAvatar />
                        <div>
                          <ShimmerLine width="120px" height="14px" />
                          <ShimmerLine width="160px" height="12px" style={{ marginTop: '6px' }} />
                        </div>
                      </ShimmerRegistroHeader>
                      <ShimmerLine width="100%" height="14px" />
                      <ShimmerLine width="90%" height="14px" style={{ marginTop: '8px' }} />
                      <ShimmerLine width="75%" height="14px" style={{ marginTop: '8px' }} />
                    </ShimmerRegistroItem>
                  ))}
                </RegistrosList>
              ) : registros.length === 0 ? (
                <EmptyRegistrosState>
                  <EmptyRegistrosIcon>
                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </EmptyRegistrosIcon>
                  <EmptyRegistrosText>Nenhum registro adicionado ainda</EmptyRegistrosText>
                </EmptyRegistrosState>
              ) : (
                <RegistrosList>
                  {registros.map(registro => (
                    <RegistroItem key={registro.id}>
                      {editingRegistro === registro.id ? (
                        <EditRegistroForm>
                          <EditTextArea
                            value={editingText}
                            onChange={(e) => onEditTextChange && onEditTextChange(e.target.value)}
                            rows={4}
                            autoFocus
                          />
                          <EditActions>
                            <CancelEditButton onClick={() => onCancelEdit && onCancelEdit()}>
                              Cancelar
                            </CancelEditButton>
                            <SaveButton 
                              onClick={() => onSaveEdit && onSaveEdit(registro.id)}
                              disabled={!editingText.trim() || savingRegistroId === registro.id}
                            >
                              {savingRegistroId === registro.id ? 'Salvando...' : 'Salvar'}
                            </SaveButton>
                          </EditActions>
                        </EditRegistroForm>
                      ) : (
                        <>
                          <RegistroHeader>
                            <RegistroUserInfo>
                              <RegistroUserAvatar>
                                {registro.usuario.nome.charAt(0).toUpperCase()}
                              </RegistroUserAvatar>
                              <div>
                                <RegistroUser>{registro.usuario.nome}</RegistroUser>
                                <RegistroDate>
                                  {new Date(registro.created_at).toLocaleString('pt-BR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                  {registro.updated_at !== registro.created_at && ' (editado)'}
                                </RegistroDate>
                              </div>
                            </RegistroUserInfo>
                            <RegistroMenuContainer data-registro-menu>
                              {(canEditUpdate && canEditUpdate(registro) || canDeleteUpdate && canDeleteUpdate(registro)) && (
                                <>
                                  <RegistroMenuButton 
                                    onClick={() => onMenuToggle && onMenuToggle(openMenuId === registro.id ? null : registro.id)}
                                  >
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                    </svg>
                                  </RegistroMenuButton>
                                  {openMenuId === registro.id && (
                                    <RegistroMenu>
                                      {canEditUpdate && canEditUpdate(registro) && (
                                        <RegistroMenuItem 
                                          onClick={() => {
                                            onEditRegistro && onEditRegistro(registro.id);
                                            onMenuToggle && onMenuToggle(null);
                                          }}
                                        >
                                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                          Editar
                                        </RegistroMenuItem>
                                      )}
                                      {canDeleteUpdate && canDeleteUpdate(registro) && (
                                        <RegistroMenuItem 
                                          $danger
                                          onClick={() => {
                                            setPendingDeleteRegistroId(registro.id);
                                            onMenuToggle && onMenuToggle(null);
                                          }}
                                          disabled={deletingRegistroId === registro.id}
                                        >
                                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                          </svg>
                                          {deletingRegistroId === registro.id ? (
                                            <><SpinnerCircle /> Excluindo...</>
                                          ) : 'Excluir'}
                                        </RegistroMenuItem>
                                      )}
                                    </RegistroMenu>
                                  )}
                                </>
                              )}
                            </RegistroMenuContainer>
                          </RegistroHeader>
                          <RegistroText>{registro.descricao_atualizacao}</RegistroText>
                        </>
                      )}
                    </RegistroItem>
                  ))}
                </RegistrosList>
              )}
            </RegistrosContainer>
            )}
            </DesktopOnlyRegistrosTab>
        </ModalBody>

        {canViewUpdates && (
          <RegistrosPanel>
            <RegistrosPanelHeader>
              <RegistrosPanelTitle>
                Registros
                <RegistrosPanelCount>{registros.length + historico.length}</RegistrosPanelCount>
              </RegistrosPanelTitle>
              <RegistrosFilterTabs>
                {(['tudo', 'comentarios', 'historico'] as const).map(f => (
                  <RegistrosFilterTab key={f} $active={registrosFilter === f} onClick={() => setRegistrosFilter(f)}>
                    {f === 'tudo' ? 'Tudo' : f === 'comentarios' ? 'Comentários' : 'Histórico'}
                  </RegistrosFilterTab>
                ))}
              </RegistrosFilterTabs>
            </RegistrosPanelHeader>
            <RegistrosPanelBody>
              {(loadingRegistros || loadingHistorico) ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <TimelineItem key={i}>
                      <TimelineAvatarCol>
                        <ShimmerAvatar />
                        <TimelineLine />
                      </TimelineAvatarCol>
                      <TimelineContent>
                        <ShimmerLine width="80%" height="13px" />
                        <ShimmerLine width="55%" height="12px" style={{ marginTop: '6px' }} />
                      </TimelineContent>
                    </TimelineItem>
                  ))}
                </>
              ) : (() => {
                type TEntry =
                  | { kind: 'registro'; data: Registro; ts: Date }
                  | { kind: 'historico'; data: HistoricoItem; ts: Date };

                const allEntries: TEntry[] = [];
                if (registrosFilter !== 'historico') {
                  registros.forEach(r => allEntries.push({ kind: 'registro', data: r, ts: new Date(r.created_at) }));
                }
                if (registrosFilter !== 'comentarios') {
                  historico.forEach(h => allEntries.push({ kind: 'historico', data: h, ts: new Date(h.data_hora) }));
                }
                allEntries.sort((a, b) => b.ts.getTime() - a.ts.getTime());

                if (allEntries.length === 0) return (
                  <EmptyRegistrosState>
                    <EmptyRegistrosIcon>
                      <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </EmptyRegistrosIcon>
                    <EmptyRegistrosText>Nenhum registro ainda</EmptyRegistrosText>
                  </EmptyRegistrosState>
                );

                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                const toDateStr = (d: Date) => d.toISOString().slice(0, 10);
                let lastGroup = '';
                const elements: React.ReactNode[] = [];

                allEntries.forEach((entry, idx) => {
                  const d = entry.ts;
                  const ds = toDateStr(d);
                  let label = '';
                  if (ds === toDateStr(today)) label = 'Hoje';
                  else if (ds === toDateStr(yesterday)) {
                    label = `Ontem · ${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}`;
                  } else {
                    label = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                  }
                  if (label !== lastGroup) {
                    elements.push(<RegistroDateGroup key={`grp-${ds}-${idx}`}>{label}</RegistroDateGroup>);
                    lastGroup = label;
                  }
                  const isLast = idx === allEntries.length - 1;
                  const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                  if (entry.kind === 'historico') {
                    const h = entry.data;
                    const authorName = h.usuario?.nome || 'Sistema';
                    const isSelf = userData && h.usuario && h.usuario.id === userData.id;
                    elements.push(
                      <TimelineItem key={`h-${h.id}`}>
                        <TimelineAvatarCol>
                          <TimelineAvatar style={{ background: '#f0fdf4', color: '#16a34a' }}>
                            <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                              <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                          </TimelineAvatar>
                          {!isLast && <TimelineLine />}
                        </TimelineAvatarCol>
                        <TimelineContent>
                          <TimelineHistoryRow>
                            <TimelineHistoryText>
                              <TimelineAuthor>{isSelf ? 'Você' : authorName}</TimelineAuthor>
                              {' '}{h.observacao}
                            </TimelineHistoryText>
                            <TimelineTime>{timeStr}</TimelineTime>
                          </TimelineHistoryRow>
                        </TimelineContent>
                      </TimelineItem>
                    );
                  } else {
                    const registro = entry.data;
                    const isSelf = userData && registro.usuario.id === userData.id;
                    const isHistory = !!registro.status_na_data;

                    if (editingRegistro === registro.id) {
                      elements.push(
                        <TimelineItem key={registro.id}>
                          <TimelineAvatarCol>
                            <TimelineAvatar>{registro.usuario.nome.charAt(0).toUpperCase()}</TimelineAvatar>
                            {!isLast && <TimelineLine />}
                          </TimelineAvatarCol>
                          <TimelineContent>
                            <EditRegistroForm>
                              <EditTextArea value={editingText} onChange={(e) => onEditTextChange && onEditTextChange(e.target.value)} rows={3} autoFocus />
                              <EditActions>
                                <CancelEditButton onClick={() => onCancelEdit && onCancelEdit()}>Cancelar</CancelEditButton>
                                <SaveButton onClick={() => onSaveEdit && onSaveEdit(registro.id)} disabled={!editingText.trim() || savingRegistroId === registro.id}>
                                  {savingRegistroId === registro.id ? 'Salvando...' : 'Salvar'}
                                </SaveButton>
                              </EditActions>
                            </EditRegistroForm>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    } else if (isHistory) {
                      elements.push(
                        <TimelineItem key={registro.id}>
                          <TimelineAvatarCol>
                            <TimelineAvatar>{registro.usuario.nome.charAt(0).toUpperCase()}</TimelineAvatar>
                            {!isLast && <TimelineLine />}
                          </TimelineAvatarCol>
                          <TimelineContent>
                            <TimelineHistoryRow>
                              <TimelineHistoryText>
                                <TimelineAuthor>{isSelf ? 'Você' : registro.usuario.nome}</TimelineAuthor>
                                {' alterou o status para '}
                                <StatusBadge status={registro.status_na_data!} style={{ fontSize: '11px', padding: '2px 8px' }}>
                                  {statusLabels[registro.status_na_data!]}
                                </StatusBadge>
                              </TimelineHistoryText>
                              <TimelineTime>{timeStr}</TimelineTime>
                            </TimelineHistoryRow>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    } else {
                      elements.push(
                        <TimelineItem key={registro.id}>
                          <TimelineAvatarCol>
                            <TimelineAvatar>
                              <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 16, height: 16 }}>
                                <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </TimelineAvatar>
                            {!isLast && <TimelineLine />}
                          </TimelineAvatarCol>
                          <TimelineContent>
                            <TimelineCommentLabel>
                              <TimelineAuthor>{isSelf ? 'Você' : registro.usuario.nome}</TimelineAuthor>
                              <TimelineCommentWord>comentou</TimelineCommentWord>
                              <TimelineTime>{timeStr}{registro.updated_at !== registro.created_at ? ' (editado)' : ''}</TimelineTime>
                              {(canEditUpdate && canEditUpdate(registro) || canDeleteUpdate && canDeleteUpdate(registro)) && (
                                <RegistroMenuContainer data-registro-menu style={{ position: 'relative', marginLeft: 'auto' }}>
                                  <RegistroMenuButton onClick={() => onMenuToggle && onMenuToggle(openMenuId === registro.id ? null : registro.id)}>
                                    <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                    </svg>
                                  </RegistroMenuButton>
                                  {openMenuId === registro.id && (
                                    <RegistroMenu>
                                      {canEditUpdate && canEditUpdate(registro) && (
                                        <RegistroMenuItem onClick={() => { onEditRegistro && onEditRegistro(registro.id); onMenuToggle && onMenuToggle(null); }}>
                                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                          Editar
                                        </RegistroMenuItem>
                                      )}
                                      {canDeleteUpdate && canDeleteUpdate(registro) && (
                                        <RegistroMenuItem $danger onClick={() => { setPendingDeleteRegistroId(registro.id); onMenuToggle && onMenuToggle(null); }} disabled={deletingRegistroId === registro.id}>
                                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                          {deletingRegistroId === registro.id ? (<><SpinnerCircle /> Excluindo...</>) : 'Excluir'}
                                        </RegistroMenuItem>
                                      )}
                                    </RegistroMenu>
                                  )}
                                </RegistroMenuContainer>
                              )}
                            </TimelineCommentLabel>
                            <TimelineCommentBubble>
                              <TimelineCommentText>{registro.descricao_atualizacao}</TimelineCommentText>
                            </TimelineCommentBubble>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    }
                  }
                });
                return elements;
              })()}
            </RegistrosPanelBody>
            {canCreateUpdates && (
              <RegistrosPanelFooter>
                <PanelInputBox>
                  <PanelTextArea
                    placeholder="Adicione um registro ou comentário..."
                    value={newRegistro}
                    onChange={(e) => onNewRegistroChange && onNewRegistroChange(e.target.value)}
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        onAddRegistro && onAddRegistro();
                      }
                    }}
                  />
                  <PanelInputFooter>
                    <PanelSendRow>
                      <PanelSendButton onClick={() => onAddRegistro && onAddRegistro()} disabled={!newRegistro.trim() || addingRegistro}>
                        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ width: 13, height: 13 }}>
                          <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                        </svg>
                        {addingRegistro ? 'Enviando...' : 'Enviar'}
                      </PanelSendButton>
                    </PanelSendRow>
                  </PanelInputFooter>
                </PanelInputBox>
              </RegistrosPanelFooter>
            )}
          </RegistrosPanel>
        )}
        </ModalLayout>
    </>
  );

  if (contained) {
    return (
      <ModalContainer
        onClick={(e) => e.stopPropagation()}
        className="modal-container"
        $slideOver={false}
        $closing={false}
        style={{ width: '100%', height: '100%', maxWidth: '100%', borderRadius: 0, boxShadow: 'none' }}
      >
        {innerContent}
        {showDeleteConfirm && (
          <ConfirmOverlay onClick={() => setShowDeleteConfirm(false)}>
            <ConfirmModal onClick={(e) => e.stopPropagation()}>
              <ConfirmHeader>
                <ConfirmIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </ConfirmIcon>
                <ConfirmTitle>Excluir Demanda</ConfirmTitle>
              </ConfirmHeader>
              <ConfirmContent>
                <ConfirmText>
                  Tem certeza que deseja excluir a demanda <strong>"{currentDemand?.titulo}"</strong>?
                </ConfirmText>
                <ConfirmWarning>
                  Esta ação não pode ser desfeita. Todos os dados relacionados serão removidos permanentemente.
                </ConfirmWarning>
              </ConfirmContent>
              <ConfirmActions>
                <ConfirmCancelButton onClick={() => setShowDeleteConfirm(false)}>
                  Cancelar
                </ConfirmCancelButton>
                <ConfirmDeleteButton onClick={handleDelete}>
                  Excluir Demanda
                </ConfirmDeleteButton>
              </ConfirmActions>
            </ConfirmModal>
          </ConfirmOverlay>
        )}
        {pendingDeleteRegistroId !== null && (
          <ConfirmOverlay onClick={() => { if (!deletingRegistroId) setPendingDeleteRegistroId(null); }}>
            <ConfirmModal onClick={(e) => e.stopPropagation()}>
              <ConfirmHeader>
                <ConfirmIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </ConfirmIcon>
                <ConfirmTitle>Excluir Atualização</ConfirmTitle>
              </ConfirmHeader>
              <ConfirmContent>
                <ConfirmText>Tem certeza que deseja excluir esta atualização?</ConfirmText>
                <ConfirmWarning>Esta ação não pode ser desfeita.</ConfirmWarning>
              </ConfirmContent>
              <ConfirmActions>
                <ConfirmCancelButton onClick={() => setPendingDeleteRegistroId(null)} disabled={deletingRegistroId === pendingDeleteRegistroId}>
                  Cancelar
                </ConfirmCancelButton>
                <ConfirmDeleteButton
                  onClick={() => { if (pendingDeleteRegistroId !== null) onDeleteRegistro && onDeleteRegistro(pendingDeleteRegistroId); }}
                  disabled={deletingRegistroId === pendingDeleteRegistroId}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  {deletingRegistroId === pendingDeleteRegistroId ? (<><SpinnerCircle />Excluindo...</>) : 'Excluir Atualização'}
                </ConfirmDeleteButton>
              </ConfirmActions>
            </ConfirmModal>
          </ConfirmOverlay>
        )}
      </ModalContainer>
    );
  }

  return (
    <>
      <DemandDetailModalMobile {...mobileProps} />
      <Overlay onClick={slideOver ? undefined : handleClose} $slideOver={slideOver}>
      <ModalContainer onClick={(e) => e.stopPropagation()} className="modal-container" $slideOver={slideOver} $closing={isClosing}>
        {innerContent}
        {showDeleteConfirm && (
          <ConfirmOverlay onClick={() => setShowDeleteConfirm(false)}>
            <ConfirmModal onClick={(e) => e.stopPropagation()}>
              <ConfirmHeader>
                <ConfirmIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </ConfirmIcon>
                <ConfirmTitle>Excluir Demanda</ConfirmTitle>
              </ConfirmHeader>
              <ConfirmContent>
                <ConfirmText>
                  Tem certeza que deseja excluir a demanda <strong>"{currentDemand?.titulo}"</strong>?
                </ConfirmText>
                <ConfirmWarning>
                  Esta ação não pode ser desfeita. Todos os dados relacionados serão removidos permanentemente.
                </ConfirmWarning>
              </ConfirmContent>
              <ConfirmActions>
                <ConfirmCancelButton onClick={() => setShowDeleteConfirm(false)}>
                  Cancelar
                </ConfirmCancelButton>
                <ConfirmDeleteButton onClick={handleDelete}>
                  Excluir Demanda
                </ConfirmDeleteButton>
              </ConfirmActions>
            </ConfirmModal>
          </ConfirmOverlay>
        )}
        {pendingDeleteRegistroId !== null && (
          <ConfirmOverlay onClick={() => { if (!deletingRegistroId) setPendingDeleteRegistroId(null); }}>
            <ConfirmModal onClick={(e) => e.stopPropagation()}>
              <ConfirmHeader>
                <ConfirmIcon>
                  <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </ConfirmIcon>
                <ConfirmTitle>Excluir Atualização</ConfirmTitle>
              </ConfirmHeader>
              <ConfirmContent>
                <ConfirmText>Tem certeza que deseja excluir esta atualização?</ConfirmText>
                <ConfirmWarning>Esta ação não pode ser desfeita.</ConfirmWarning>
              </ConfirmContent>
              <ConfirmActions>
                <ConfirmCancelButton onClick={() => setPendingDeleteRegistroId(null)} disabled={deletingRegistroId === pendingDeleteRegistroId}>
                  Cancelar
                </ConfirmCancelButton>
                <ConfirmDeleteButton
                  onClick={() => { if (pendingDeleteRegistroId !== null) onDeleteRegistro && onDeleteRegistro(pendingDeleteRegistroId); }}
                  disabled={deletingRegistroId === pendingDeleteRegistroId}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  {deletingRegistroId === pendingDeleteRegistroId ? (<><SpinnerCircle />Excluindo...</>) : 'Excluir Atualização'}
                </ConfirmDeleteButton>
              </ConfirmActions>
            </ConfirmModal>
          </ConfirmOverlay>
        )}
      </ModalContainer>
    </Overlay>
    </>
  );
};

// Styled Components
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinnerCircle = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const Overlay = styled.div<{ $slideOver?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${p => p.$slideOver ? 'transparent' : 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${p => p.$slideOver ? 4200 : 2001};
  pointer-events: ${p => p.$slideOver ? 'none' : 'auto'};

  @media (max-width: 768px) {
    align-items: flex-end;
  }
  @media (max-width: 900px) {
    display: none;
  }
`;

const ModalContainer = styled.div<{ $slideOver?: boolean; $closing?: boolean }>`
  background: white;
  border-radius: 16px;
  width: 92%;
  max-width: 1060px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
  ${p => p.$slideOver && !p.$closing && css`animation: ${slideInFromRight} 0.32s cubic-bezier(0.32, 0, 0.15, 1) both;`}
  ${p => p.$slideOver && p.$closing && css`animation: ${slideOutToRight} 0.30s cubic-bezier(0.32, 0, 0.15, 1) both;`}

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    height: 92vh;
    border-radius: 20px 20px 0 0;
  }
`;

const ModalLayout = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: row;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const RegistrosPanel = styled.div`
  width: 380px;
  min-width: 380px;
  border-left: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  min-height: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RegistrosPanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 20px 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
`;

const RegistrosFilterTabs = styled.div`
  display: flex;
  gap: 2px;
`;

const RegistrosFilterTab = styled.button<{ $active: boolean }>`
  background: ${p => p.$active ? 'white' : 'transparent'};
  border: ${p => p.$active ? '1px solid #e5e7eb' : '1px solid transparent'};
  border-radius: 7px;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: ${p => p.$active ? '600' : '400'};
  color: ${p => p.$active ? '#111827' : '#6b7280'};
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: ${p => p.$active ? '0 1px 3px rgba(0,0,0,0.06)' : 'none'};

  &:hover {
    color: #111827;
  }
`;

const RegistrosPanelTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RegistrosPanelCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 10px;
  padding: 1px 8px;
`;

const RegistrosPanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 0;

  /* Custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #e2e8f0 transparent;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
`;

const RegistrosPanelFooter = styled.div`
  flex-shrink: 0;
  padding: 12px 16px 16px 16px;
`;

const PanelInputBox = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: white;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;

const PanelTextArea = styled.textarea`
  width: 100%;
  border: none;
  padding: 12px 14px 6px 14px;
  font-size: 13px;
  font-family: inherit;
  resize: none;
  outline: none;
  color: #374151;
  background: transparent;
  box-sizing: border-box;
  display: block;

  &::placeholder {
    color: #9ca3af;
  }
`;

const PanelInputFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 6px 10px 8px 10px;
`;

const PanelIconRow = styled.div`
  display: flex;
  gap: 2px;
`;

const PanelIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: #6b7280;
    background: #f3f4f6;
  }
`;

const PanelSendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PanelSendHint = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const PanelSendButton = styled.button`
  background: #6366f1;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.88;
  }
`;

const RegistroDateGroup = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 12px 0 6px 0;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 12px;
`;

const TimelineAvatarCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
`;

const TimelineAvatar = styled.div`
  width: 34px;
  height: 34px;
  min-height: 34px;
  border-radius: 50%;
  background: #e8eaf6;
  color: #5c6bc0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const TimelineLine = styled.div`
  width: 2px;
  flex: 1;
  min-height: 16px;
  background: #e9edf2;
  margin: 4px 0;
`;

const TimelineContent = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: 12px;
  padding-top: 5px;
`;

const TimelineHistoryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 8px;
  min-height: 34px;
`;

const TimelineHistoryText = styled.span`
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
`;

const TimelineAuthor = styled.strong`
  font-weight: 700;
  color: #111827;
`;

const TimelineCommentWord = styled.span`
  font-size: 13px;
  color: #6b7280;
  font-weight: 400;
`;

const TimelineTime = styled.span`
  font-size: 12px;
  color: #9ca3af;
  flex-shrink: 0;
`;

const TimelineCommentLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  min-height: 20px;
`;

const TimelineCommentBubble = styled.div`
  background: white;
  border: 1px solid #e9edf2;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
`;

const TimelineCommentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const TimelineCommentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TimelineCommentText = styled.p`
  font-size: 13px;
  color: #374151;
  margin: 0;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

const LeftPanelContent = styled.div<{ $hidden?: boolean }>`
  @media (max-width: 768px) {
    display: ${props => props.$hidden ? 'none' : 'block'};
  }
`;

const ConcluirButton = styled.button`
  background: white;
  color: #374151;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s;

  svg {
    width: 15px;
    height: 15px;
    color: #22c55e;
  }

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e5e7eb;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 16px;
    gap: 10px;
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

const ModalTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 17px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background-color: #f3f4f6;
    color: #111827;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
  margin-right: 4px;
  &:hover { background: #e5e7eb; }
  svg { width: 16px; height: 16px; }
`;

const ModalBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const DesktopOnlyRegistrosTab = styled.div`
  @media (min-width: 769px) {
    display: none;
  }
`;

const Section = styled.div`
  margin-bottom: 24px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

const Description = styled.p`
  font-size: 14px;
  color: #4b5563;
  line-height: 1.5;
  margin: 0;
  white-space: pre-wrap;
`;

const statusConfig: Record<DemandStatus, { bg: string; color: string; dot: string }> = {
  NAO_INICIADA: { bg: '#fee2e2', color: '#dc2626', dot: '#ef4444' },
  EM_ANDAMENTO: { bg: '#dbeafe', color: '#1d4ed8', dot: '#3b82f6' },
  PAUSADA:      { bg: '#fef9c3', color: '#a16207', dot: '#f59e0b' },
  CONCLUIDA:    { bg: '#dcfce7', color: '#059669', dot: '#10b981' },
  CANCELADA:    { bg: '#f1f5f9', color: '#64748b', dot: '#94a3b8' },
};

const StatusBadge = styled.span<{ status: DemandStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
  background-color: ${({ status }) => statusConfig[status]?.bg ?? '#f1f5f9'};
  color: ${({ status }) => statusConfig[status]?.color ?? '#64748b'};
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${({ status }) => statusConfig[status]?.dot ?? '#94a3b8'};
    flex-shrink: 0;
  }
`;

const SLABadge = styled.span<{ color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
  background-color: ${({ color }) => color}20;
  color: ${({ color }) => color};
`;

const FormGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Select = styled.select`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #9ca3af;
  }

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }

  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: var(--accent);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: var(--accent-dark);
  }

  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
`;

// New styled components for redesigned layout
const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: var(--accent-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
  }
`;

const CancelButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 13px;
  }
`;

const Properties = styled.div`
  display: flex;
  flex-direction: column;
  padding: 4px 0 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const PropertiesSection = styled.div`
  margin-bottom: 24px;
`;

const PropertiesLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 10px;
`;

const PropertiesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PropertyRow = styled.div`
  display: grid;
  grid-template-columns: 130px 1fr;
  align-items: center;
  gap: 8px;
  height: 34px;
  overflow: visible;

  @media (max-width: 768px) {
    grid-template-columns: 120px 1fr;
    height: auto;
    min-height: 34px;
  }
`;

const HeaderStatusCell = styled.div<{ $canEdit?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  cursor: ${p => p.$canEdit ? 'pointer' : 'default'};
  flex-shrink: 0;
  border-radius: 8px;
  padding: 2px 4px;
  margin: -2px -4px;
  transition: background 0.15s;

  &:hover {
    background: ${p => p.$canEdit ? 'rgba(0,0,0,0.04)' : 'transparent'};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const PropertyLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;

  svg {
    width: 14px;
    height: 14px;
    color: #9ca3af;
    flex-shrink: 0;
  }
`;

const PropertyValue = styled.div`
  font-size: 14px;
  color: #111827;
  min-width: 0;
  display: flex;
  align-items: center;
`;

const DescriptionHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 8px;
`;

const ResponsibleInline = styled.div<{ $canEdit: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 6px;
  cursor: ${p => p.$canEdit ? 'pointer' : 'default'};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${p => p.$canEdit ? 'rgba(15, 23, 42, 0.05)' : 'transparent'};
  }
`;

const ResponsibleInlineName = styled.span`
  font-size: 14px;
  color: #111827;
  font-weight: 500;
`;

const CustomSelectWrapper = styled.div`
  position: relative;
  min-width: 0;
  width: 100%;
`;

const CustomSelectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 3px 7px;
  background: white;
  border: 1.5px solid var(--accent);
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.4;
  color: #111827;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: border-color 0.15s;

  svg {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
    color: #6b7280;
  }

  &:focus {
    outline: none;
  }
`;

const CustomSelectDropdown = styled.div`
  position: absolute;
  top: calc(100% + 3px);
  left: 0;
  min-width: 180px;
  width: max-content;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 2003;
  overflow: hidden;
`;

const CustomSelectOption = styled.div`
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: #f8fafc;
  }
`;

const DropdownSearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #1e293b;
  box-sizing: border-box;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    border-bottom-color: var(--accent);
  }
`;

const CustomSelectOptionsContainer = styled.div`
  max-height: 250px;
  overflow-y: auto;
`;

const Input = styled.input`
  width: 100%;
  padding: 3px 7px;
  border: 1.5px solid var(--accent);
  border-radius: 4px;
  font-size: 14px;
  color: #111827;
  line-height: 1.4;
  background: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: var(--accent);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const EditField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DangerZone = styled.div`
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px dashed #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const DangerZoneDescription = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
  line-height: 1.5;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: none;
  color: #ef4444;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 0.2s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover:not(:disabled) {
    color: #dc2626;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ConfirmOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 2003;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConfirmModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const ConfirmHeader = styled.div`
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const ConfirmIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fef2f2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;

  svg {
    width: 28px;
    height: 28px;
  }
`;

const ConfirmTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ConfirmContent = styled.div`
  padding: 0 24px 24px;
  text-align: center;
`;

const ConfirmText = styled.p`
  font-size: 15px;
  color: #374151;
  margin: 0 0 12px 0;
  line-height: 1.5;

  strong {
    color: #111827;
    font-weight: 600;
  }
`;

const ConfirmWarning = styled.p`
  font-size: 13px;
  color: #dc2626;
  margin: 0;
  line-height: 1.5;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`;

const ConfirmCancelButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const ConfirmDeleteButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #b91c1c;
  }
`;

const HoverShell = styled.div<{ $canEdit: boolean; $block?: boolean }>`
  display: ${p => p.$block ? 'block' : 'inline-flex'};
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  margin: -4px -8px;
  border-radius: 6px;
  cursor: ${p => p.$canEdit ? 'pointer' : 'default'};
  transition: background-color 0.15s ease;
  min-height: 24px;
  ${p => p.$block ? 'flex: 1; min-width: 0;' : ''}

  &:hover {
    background: ${p => p.$canEdit ? 'rgba(15, 23, 42, 0.05)' : 'transparent'};
  }
`;

const HeaderResponsible = styled.div<{ $canEdit: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 8px;
  cursor: ${p => p.$canEdit ? 'pointer' : 'default'};
  flex-shrink: 0;
  max-width: 200px;
  transition: background-color 0.15s ease;

  &:hover {
    background: ${p => p.$canEdit ? 'rgba(15, 23, 42, 0.05)' : 'transparent'};
  }

  @media (max-width: 768px) {
    max-width: 100%;
    flex-basis: 100%;
    order: 3;
    margin-top: 4px;
  }
`;

const HeaderResponsibleName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const ResponsibleDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: -12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 2003;
  min-width: 260px;
  max-height: 320px;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: 768px) {
    left: auto;
    right: 0;
    min-width: 240px;
  }
`;

const ResponsibleOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: #f8fafc;
  }

  &:first-child {
    border-radius: 11px 11px 0 0;
  }

  &:last-child {
    border-radius: 0 0 11px 11px;
  }
`;

const OptionAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OptionInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const OptionName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
`;

const OptionRole = styled.div`
  font-size: 11.5px;
  color: #94a3b8;
  line-height: 1.3;
`;

const StatusCell = styled.div<{ $canEdit?: boolean }>`
  position: relative;
  cursor: ${props => props.$canEdit ? 'pointer' : 'default'};
  width: fit-content;
  display: flex;
  align-items: center;
`;

const StatusDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,.06), 0 10px 30px -4px rgba(0,0,0,.12);
  z-index: 2002;
  min-width: 190px;
  overflow: hidden;
  padding: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatusOption = styled.div<{ $status: DemandStatus }>`
  padding: 7px 10px;
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.12s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: #f1f5f9; }
`;

const StatusOptionPill = styled.span<{ $status: DemandStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $status }) => statusConfig[$status]?.bg ?? '#f1f5f9'};
  color: ${({ $status }) => statusConfig[$status]?.color ?? '#64748b'};
  &::before {
    content: '';
    width: 6px; height: 6px;
    border-radius: 50%;
    background: ${({ $status }) => statusConfig[$status]?.dot ?? '#94a3b8'};
    flex-shrink: 0;
  }
`;

const UnassignedText = styled.span`
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
`;

const LoadingSpinner = styled.span`
  font-size: 13px;
  color: #64748b;
  font-style: italic;
`;

// Styled Components para Tabs e Registros
const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 24px;
  gap: 8px;
  flex-shrink: 0;

  @media (min-width: 769px) {
    display: none;
  }

  @media (max-width: 768px) {
    padding: 0 16px;
    gap: 0;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  padding: 16px 20px;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$active ? 'var(--accent)' : '#64748b'};
  cursor: pointer;
  border-bottom: 2px solid ${props => props.$active ? 'var(--accent)' : 'transparent'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: var(--accent);
  }

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    padding: 12px 8px;
    font-size: 13px;
  }
`;

const RegistrosContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;

  @media (max-width: 768px) {
    padding: 12px 0;
    gap: 12px;
  }
`;

const LoadingRegistros = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #64748b;
  font-size: 14px;
`;

const RegistrosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EmptyRegistrosState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
`;

const EmptyRegistrosIcon = styled.div`
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  color: #cbd5e1;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const EmptyRegistrosText = styled.p`
  color: #94a3b8;
  font-size: 15px;
  margin: 0;
`;

const RegistroItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
`;

const RegistroHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const RegistroUserInfo = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const RegistroUserAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
`;

const RegistroUser = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 2px;
`;

const RegistroDate = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const RegistroMenuContainer = styled.div`
  position: relative;
`;

const RegistroMenuButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background: #f1f5f9;
    color: #1e293b;
  }
`;

const RegistroMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 140px;
  margin-top: 4px;
  overflow: hidden;
`;

const RegistroMenuItem = styled.button<{ $danger?: boolean }>`
  width: 100%;
  background: none;
  border: none;
  padding: 10px 16px;
  text-align: left;
  cursor: pointer;
  color: ${props => props.$danger ? '#ef4444' : '#334155'};
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: ${props => props.$danger ? '#fef2f2' : '#f8fafc'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const RegistroText = styled.p`
  font-size: 14px;
  color: #334155;
  margin: 0 0 12px 0;
  line-height: 1.6;
  white-space: pre-wrap;
`;

const EditRegistroForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EditTextArea = styled.textarea`
  padding: 12px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #1e293b;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const CancelEditButton = styled.button`
  background: #f1f5f9;
  color: #64748b;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
  }
`;

const NewRegistroSection = styled.div`
  position: relative;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 20px;
`;

const NewRegistroTextArea = styled.textarea`
  padding: 12px 16px;
  padding-bottom: 52px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #1e293b;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.2s ease;
  width: 100%;
  box-sizing: border-box;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    background: white;
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(var(--accent-rgb), 0.1);
  }
`;

const AddRegistroButton = styled.button`
  position: absolute;
  bottom: 28px;
  right: 8px;
  padding: 8px 20px;
  background: var(--accent);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--accent-dark);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Shimmer Components
const ShimmerRegistroItem = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
`;

const ShimmerRegistroHeader = styled.div`
  margin-bottom: 12px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const ShimmerAvatar = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

const ShimmerLine = styled.div<{ width?: string; height?: string }>`
  width: ${props => props.width || '100%'};
  height: ${props => props.height || '14px'};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    #e2e8f0 0%,
    #f1f5f9 50%,
    #e2e8f0 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

// ============ Inline edit wrappers (private to this modal) ============

interface InlineEditTextProps {
  value: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (value: string) => void | Promise<void>;
  placeholder?: string;
  emptyLabel?: string;
  renderDisplay?: (value: string) => React.ReactNode;
  inputStyle?: React.CSSProperties;
  allowEmpty?: boolean;
  displayBlock?: boolean;
}

const InlineEditText: React.FC<InlineEditTextProps> = ({
  value, canEdit, saving, onSave, placeholder, emptyLabel = '-',
  renderDisplay, inputStyle, allowEmpty = true, displayBlock = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) setDraft(value); }, [editing, value]);

  useLayoutEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (!allowEmpty && !next) return;
    if (next !== (value ?? '')) onSave(next);
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        value={draft}
        placeholder={placeholder}
        style={inputStyle}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }

  const displayValue = value || emptyLabel;
  return (
    <HoverShell
      $canEdit={canEdit}
      $block={displayBlock}
      onClick={() => canEdit && setEditing(true)}
    >
      {saving ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(displayValue) : displayValue)}
    </HoverShell>
  );
};

interface InlineEditTextAreaProps {
  value: string;
  canEdit: boolean;
  saving: boolean;
  onSave: (value: string) => void | Promise<void>;
  placeholder?: string;
  emptyLabel?: string;
}

const InlineEditTextArea: React.FC<InlineEditTextAreaProps> = ({
  value, canEdit, saving, onSave, placeholder, emptyLabel = '-',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<number>(0);

  const beginEdit = () => {
    const body = document.querySelector('[data-modal-body]') as HTMLElement | null;
    scrollRef.current = body?.scrollTop ?? 0;
    setDraft(value ?? '');
    setEditing(true);
  };

  useLayoutEffect(() => {
    if (!editing) return;
    const body = document.querySelector('[data-modal-body]') as HTMLElement | null;
    if (body) body.scrollTop = scrollRef.current;
    if (taRef.current) {
      taRef.current.focus();
      taRef.current.setSelectionRange(draft.length, draft.length);
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== (value ?? '')) onSave(draft);
  };

  if (editing) {
    return (
      <TextArea
        ref={taRef}
        value={draft}
        rows={Math.max(4, draft.split('\n').length + 1)}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
        }}
      />
    );
  }

  return (
    <HoverShell
      $canEdit={canEdit}
      $block
      onClick={() => canEdit && beginEdit()}
    >
      {saving ? <SpinnerCircle /> : <Description>{value || emptyLabel}</Description>}
    </HoverShell>
  );
};

interface SelectOption { value: string; label: string }

interface InlineEditSelectProps {
  value: string | undefined;
  canEdit: boolean;
  saving: boolean;
  options: SelectOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  selectPlaceholder?: string;
  emptyLabel?: string;
  onSave: (value: string) => void | Promise<void>;
  renderDisplay?: (label: string) => React.ReactNode;
  onOpen?: () => void;
}

const InlineEditSelect: React.FC<InlineEditSelectProps> = ({
  value, canEdit, saving, options, searchable, searchPlaceholder,
  selectPlaceholder = 'Selecione', emptyLabel = '-', onSave, renderDisplay, onOpen,
}) => {
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const startEditing = () => { setEditing(true); onOpen?.(); };

  useEffect(() => {
    if (!editing) return;
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setEditing(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [editing]);

  const currentLabel = value ? (options.find(o => o.value === value)?.label ?? value) : '';

  if (!editing) {
    const display = currentLabel || emptyLabel;
    return (
      <HoverShell
        $canEdit={canEdit}
        onClick={() => canEdit && startEditing()}
      >
        {saving ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(display) : display)}
      </HoverShell>
    );
  }

  const filtered = searchable
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase())).slice(0, 50)
    : options;

  return (
    <CustomSelectWrapper ref={wrapperRef}>
      <CustomSelectButton onClick={(e) => e.stopPropagation()}>
        {currentLabel || selectPlaceholder}
        <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </CustomSelectButton>
      <CustomSelectDropdown>
        {searchable && (
          <DropdownSearchInput
            type="text"
            placeholder={searchPlaceholder ?? 'Buscar...'}
            value={search}
            autoFocus
            onChange={(e) => setSearch(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        <CustomSelectOptionsContainer>
          {filtered.map((opt) => (
            <CustomSelectOption
              key={opt.value}
              onClick={() => {
                setEditing(false);
                setSearch('');
                if (opt.value !== value) onSave(opt.value);
              }}
            >
              {opt.label}
            </CustomSelectOption>
          ))}
        </CustomSelectOptionsContainer>
      </CustomSelectDropdown>
    </CustomSelectWrapper>
  );
};

interface InlineEditDateProps {
  value: string | undefined;
  canEdit: boolean;
  saving: boolean;
  onSave: (value: string | null) => void | Promise<void>;
  emptyLabel?: string;
  renderDisplay?: (formatted: string) => React.ReactNode;
}

const InlineEditDate: React.FC<InlineEditDateProps> = ({
  value, canEdit, saving, onSave, emptyLabel = '-', renderDisplay,
}) => {
  const [editing, setEditing] = useState(false);
  const isoValue = value ? new Date(value).toISOString().slice(0, 10) : '';
  const [draft, setDraft] = useState(isoValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) setDraft(isoValue); }, [editing, isoValue]);

  useLayoutEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft !== isoValue) onSave(draft || null);
  };

  if (editing) {
    return (
      <Input
        ref={inputRef}
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          if (e.key === 'Escape') { setDraft(isoValue); setEditing(false); }
        }}
      />
    );
  }

  const formatted = value ? new Date(value).toLocaleDateString('pt-BR') : emptyLabel;
  return (
    <HoverShell
      $canEdit={canEdit}
      onClick={() => canEdit && setEditing(true)}
    >
      {saving ? <SpinnerCircle /> : (renderDisplay ? renderDisplay(formatted) : formatted)}
    </HoverShell>
  );
};
